import { Avatar, Card, Colors, Text } from '@amelio/ui-library';

import type { QuestionComment } from './types';

import { useAuthorSignature } from '@/hooks/useAuthorSignature';
import { getFullName } from '@/utils/user';

interface ReplyItemProps {
    reply: QuestionComment;
    dataTestId?: string;
}

export const ReplyItem = ({ reply, dataTestId = 'reply-item' }: ReplyItemProps) => {
    const signature = useAuthorSignature({
        authorName: reply.user && getFullName(reply.user),
        authorPictureUrl: reply.user?.profilePictureUrl,
        isAnonymousRespondent: reply.isAnonymousRespondent,
        writtenAt: reply.timestamp,
    });

    return (
        <div
            key={reply.id}
            className={'grid w-full grid-cols-[auto_auto_1fr] items-start gap-3'}
            data-testid={dataTestId}
        >
            <div className={'shrink-0'} />

            <div className={'shrink-0'}>
                <Avatar
                    alt={signature.name}
                    initials={signature.initials}
                    src={signature.pictureUrl}
                    size={'xs'}
                />
            </div>

            <Card
                padding={'none'}
                bgColor={'bg-white'}
                className={
                    'border-base-100 flex min-w-0 flex-col overflow-hidden rounded-lg border-t border-b px-2 py-1.5'
                }
            >
                <div className={'min-w-0 items-center justify-between gap-2 sm:flex'}>
                    <Text
                        color={Colors.BASE}
                        weight={'semibold'}
                        className={'min-w-0 leading-tight'}
                    >
                        {signature.name}
                    </Text>

                    {signature.writtenAgo && (
                        <Text
                            color={Colors.BASE_400}
                            className={'shrink-0'}
                        >
                            {signature.writtenAgo}
                        </Text>
                    )}
                </div>

                <Text
                    color={Colors.BASE}
                    className={'mt-2 leading-relaxed break-words whitespace-pre-wrap'}
                >
                    {reply.text}
                </Text>
            </Card>
        </div>
    );
};
